import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useOrgLoginDialogOptional } from '@/contexts/OrgLoginDialogContext';
import { useOrgPaths } from '@/hooks/useOrgPaths';
import { cn } from '@/lib/utils';

interface OrgSignInLinkProps extends ComponentPropsWithoutRef<'button'> {
  returnUrl?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Opens the org sign-in modal when inside {@link PublicOrgLayout}; falls back to the login page elsewhere.
 */
export function OrgSignInLink({
  returnUrl,
  className,
  children,
  type = 'button',
  ...buttonProps
}: OrgSignInLinkProps) {
  const orgLogin = useOrgLoginDialogOptional();
  const location = useLocation();
  const { login } = useOrgPaths();
  const effectiveReturn = returnUrl ?? `${location.pathname}${location.search}`;

  if (!orgLogin) {
    if (!login) return null;
    return (
      <Link to={login(effectiveReturn)} className={className}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type={type}
      className={cn('font-medium text-primary underline underline-offset-2', className)}
      onClick={() => orgLogin.openOrgLogin(effectiveReturn)}
      {...buttonProps}
    >
      {children}
    </button>
  );
}
